import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';
import PaymentReceipt from './PaymentReceipt';

/**
 * PaymentSection — shows outstanding balance, take-payment form, payment history with receipts.
 *
 * Props:
 *   patientId   — ObjectId
 *   API_BASE    — base API url
 *   patientName — for display
 *   patientPhone, patientUhid — for receipt
 */
const PaymentSection = ({ patientId, API_BASE, patientName, patientPhone, patientUhid, onPaymentSuccess }) => {
  const [payments, setPayments] = useState([]);
  const [outstanding, setOutstanding] = useState({ billed:0, paid:0, refunded:0, outstanding:0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount:'', mode:'cash', referenceNumber:'', notes:'' });
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splits, setSplits] = useState([
    { mode: 'cash', amount: '' },
    { mode: 'upi', amount: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [pinelabsConfig, setPinelabsConfig] = useState(null);
  const [pinelabsProcessing, setPinelabsProcessing] = useState(false);
  const [pinelabsResult, setPinelabsResult] = useState(null); // { success, message, amount }
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [razorpayLink, setRazorpayLink] = useState(null);
  const [razorpayLoading, setRazorpayLoading] = useState(false);

  const fetchPayments = async () => {
    try {
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API_BASE}/payments/patient/${patientId}`),
        axios.get(`${API_BASE}/payments/patient/${patientId}/outstanding`),
      ]);
      setPayments(pRes.data);
      setOutstanding(oRes.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchPayments();
    axios.get(`${API_BASE}/admin/clinic-settings`).then(r => setClinicSettings(r.data)).catch(() => {});
    axios.get(`${API_BASE}/pinelabs/config`).then(r => setPinelabsConfig(r.data)).catch(() => {});
    axios.get(`${API_BASE}/razorpay/config`).then(r => setRazorpayConfig(r.data)).catch(() => {});
  }, [patientId]); // eslint-disable-line

  const createRazorpayLink = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return alert('Amount dalo pehle');
    setRazorpayLoading(true); setRazorpayLink(null);
    try {
      const r = await axios.post(`${API_BASE}/razorpay/payment-link`, {
        amountRupees: amt,
        description: `Payment — ${patientName || 'Patient'}${patientUhid ? ' (' + patientUhid + ')' : ''}`,
        patientId,
      });
      setRazorpayLink(r.data);
    } catch (e) {
      alert('Razorpay link banane me error: ' + (e.response?.data?.error || e.message));
    } finally { setRazorpayLoading(false); }
  };

  const shareOnWhatsApp = () => {
    if (!razorpayLink?.shortUrl) return;
    const msg = `Namaste ${patientName || ''},\n\nAapki clinic payment ka link:\n${razorpayLink.shortUrl}\n\nAmount: ₹${form.amount}\n\nDhanyavaad — ${clinicSettings?.clinicName || 'Clinic'}`;
    const phone = (patientPhone || '').replace(/\D/g, '');
    const url = phone ? `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const copyRazorpayLink = async () => {
    if (!razorpayLink?.shortUrl) return;
    try { await navigator.clipboard.writeText(razorpayLink.shortUrl); alert('✓ Link copied'); }
    catch { alert('Copy failed — URL: ' + razorpayLink.shortUrl); }
  };

  const modeLabels = { cash:'Cash', card:'Card', upi:'UPI', cheque:'Cheque', 'bank-transfer':'Bank Transfer', insurance:'Insurance', other:'Other' };

  const handleSave = async (paymentType) => {
    if (splitEnabled) {
      const total = splits.reduce((s, sp) => s + (Number(sp.amount) || 0), 0);
      if (total <= 0) return alert('Enter valid split amounts');
      const desc = splits.filter(sp => Number(sp.amount) > 0).map(sp => `₹${Number(sp.amount).toLocaleString('en-IN')} ${modeLabels[sp.mode] || sp.mode}`).join(' + ');
      if (!window.confirm(`Confirm split payment: ${desc}?`)) return;
      setSaving(true);
      try {
        const res = await axios.post(`${API_BASE}/payments`, {
          patientId,
          amount: total,
          mode: 'split',
          splits: splits.filter(sp => Number(sp.amount) > 0).map(sp => ({ mode: sp.mode, amount: Number(sp.amount) })),
          referenceNumber: form.referenceNumber,
          notes: form.notes,
          type: paymentType || 'payment',
        });
        setForm({ amount:'', mode:'cash', referenceNumber:'', notes:'' });
        setSplitEnabled(false);
        setSplits([{ mode: 'cash', amount: '' }, { mode: 'upi', amount: '' }]);
        setShowForm(false);
        await fetchPayments();
        if (res.data) setViewReceipt(res.data);
        // Auto WhatsApp
        if (onPaymentSuccess) onPaymentSuccess(total, 'split', res.data?.voucherNumber, outstanding?.outstanding - total);
      } catch (e) { alert(e.response?.data?.error || 'Payment failed'); }
      setSaving(false);
    } else {
      if (!form.amount || Number(form.amount) <= 0) return alert('Enter a valid amount');
      if (!window.confirm(`Confirm: Record ${paymentType === 'advance' ? 'advance ' : ''}payment of ₹${Number(form.amount).toLocaleString('en-IN')} via ${modeLabels[form.mode] || form.mode}?`)) return;
      setSaving(true);
      try {
        const res = await axios.post(`${API_BASE}/payments`, {
          patientId,
          amount: Number(form.amount),
          mode: form.mode,
          referenceNumber: form.referenceNumber,
          notes: form.notes,
          type: paymentType || 'payment',
        });
        setForm({ amount:'', mode:'cash', referenceNumber:'', notes:'' });
        setShowForm(false);
        await fetchPayments();
        if (res.data) setViewReceipt(res.data);
        // Auto WhatsApp
        if (onPaymentSuccess) onPaymentSuccess(Number(form.amount), form.mode, res.data?.voucherNumber, outstanding?.outstanding - Number(form.amount));
      } catch (e) { alert(e.response?.data?.error || 'Payment failed'); }
      setSaving(false);
    }
  };

  // Step 1: Push to machine (does NOT record payment yet)
  const handlePinelabsPay = async () => {
    const amt = Number(form.amount) || outstanding.outstanding;
    if (!amt || amt <= 0) return alert('Enter amount or have outstanding balance');
    setPinelabsProcessing(true);
    setPinelabsResult(null);
    try {
      const res = await axios.post(`${API_BASE}/pinelabs/push`, {
        amount: amt,
        invoiceNo: `PAY-${Date.now()}`,
        patientName,
        patientId: null,
        paymentMode: form.mode === 'upi' ? 'upi' : form.mode === 'card' ? 'card' : 'any',
      });
      const txnId = res.data.transactionId;
      const isSandbox = res.data.sandbox;

      setPinelabsResult({
        stage: 'waiting', amount: amt, mode: form.mode || 'card',
        sandbox: isSandbox, transactionId: txnId,
      });

      // ── Auto-poll for live A910 transactions ──
      if (!isSandbox && txnId) {
        let pollCount = 0;
        const maxPolls = 40;                                    // ~2 min @ 3s interval
        const pollId = setInterval(async () => {
          pollCount++;
          try {
            const s = await axios.get(`${API_BASE}/pinelabs/status/${encodeURIComponent(txnId)}`);
            const status = (s.data.status || '').toUpperCase();
            if (status.includes('APPROVED') || status.includes('SUCCESS')) {
              clearInterval(pollId);
              // Auto-confirm — create Payment record directly
              try {
                const p = await axios.post(`${API_BASE}/payments`, {
                  patientId,
                  amount: amt,
                  mode: form.mode === 'upi' ? 'upi' : 'card',
                  referenceNumber: txnId,
                  notes: 'Pine Labs A910 (auto-polled)',
                });
                await fetchPayments();
                if (p.data) setViewReceipt(p.data);
              } catch {}
              setPinelabsResult({ stage: 'success', amount: amt, sandbox: false, auto: true });
              setForm({ amount: '', mode: 'cash', referenceNumber: '', notes: '' });
              setShowForm(false);
              setTimeout(() => setPinelabsResult(null), 4000);
            } else if (status.includes('DECLINED') || status.includes('FAILED') || status.includes('CANCELLED')) {
              clearInterval(pollId);
              setPinelabsResult({ stage: 'failed', message: s.data.status || 'Declined', amount: amt });
            } else if (pollCount >= maxPolls) {
              clearInterval(pollId);
              // Timeout — fall back to manual confirm, leave waiting stage
            }
          } catch {}
        }, 3000);
      }
    } catch (e) {
      setPinelabsResult({ stage: 'failed', message: e.response?.data?.error || 'Connection failed', amount: amt });
    }
    setPinelabsProcessing(false);
  };

  // Step 2: Staff confirms payment was received on machine
  const handlePinelabsConfirm = async () => {
    if (!pinelabsResult) return;
    const amt = pinelabsResult.amount;
    const mode = pinelabsResult.mode === 'upi' ? 'upi' : 'card';
    try {
      const res = await axios.post(`${API_BASE}/payments`, {
        patientId,
        amount: amt,
        mode,
        referenceNumber: pinelabsResult.transactionId || '',
        notes: pinelabsResult.sandbox ? 'Pine Labs (Sandbox)' : 'Pine Labs A910',
      });
      setPinelabsResult({ stage: 'success', amount: amt, sandbox: pinelabsResult.sandbox });
      await fetchPayments();
      // Show receipt
      if (res.data) setViewReceipt(res.data);
      setForm({ amount:'', mode:'cash', referenceNumber:'', notes:'' });
      setShowForm(false);
      // Auto-hide success after 4 seconds
      setTimeout(() => setPinelabsResult(null), 4000);
    } catch (e) {
      alert('Failed to record payment');
    }
  };

  // Step 2 alt: Staff cancels — payment was NOT received
  const handlePinelabsCancel = () => {
    setPinelabsResult(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    await axios.delete(`${API_BASE}/payments/${id}`);
    fetchPayments();
  };

  const fmtINR  = n => `₹ ${(n||0).toLocaleString('en-IN')}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

  const modeColors = {
    cash:'#10b981', card:'#3b82f6', upi:'#8b5cf6', cheque:'#f59e0b',
    'bank-transfer':'#0582ca', insurance:'#ec4899', other:'#6b7785',
  };

  return (
    <div>
      {/* KPI cards */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'20px'}}>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Billed</span>
          <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>{fmtINR(outstanding.billed)}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Paid</span>
          <h2 style={{margin:'4px 0 0', color:theme.success, fontSize:'1.4rem', fontWeight:'700'}}>{fmtINR(outstanding.paid)}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Refunded</span>
          <h2 style={{margin:'4px 0 0', color:theme.warning, fontSize:'1.4rem', fontWeight:'700'}}>{fmtINR(outstanding.refunded)}</h2>
        </div>
        <div style={{...statCard, borderLeft: outstanding.outstanding > 0 ? `4px solid ${theme.danger}` : `4px solid ${theme.success}`}}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Outstanding</span>
          <h2 style={{margin:'4px 0 0', color: outstanding.outstanding > 0 ? theme.danger : theme.success, fontSize:'1.4rem', fontWeight:'700'}}>{fmtINR(outstanding.outstanding)}</h2>
        </div>
      </div>

      {/* Action bar */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
        <h3 style={{margin:0, color:theme.navy}}>Payment History</h3>
        <button onClick={()=>setShowForm(!showForm)}
          style={{padding:'9px 20px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px'}}>
          {showForm ? '✕ Cancel' : '+ Take Payment'}
        </button>
      </div>

      {/* Take Payment Form */}
      {showForm && (
        <div style={{...cardStyle, marginBottom:'18px', backgroundColor:'#f8fffe', border:`1px solid ${theme.primary}40`}}>
          <h4 style={{margin:'0 0 14px', color:theme.primaryDark}}>Receive Payment — {patientName}</h4>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
            <div>
              <label style={labelStyle}>Amount *</label>
              <input type="number" placeholder="₹ 0" value={form.amount}
                onChange={e=>setForm({...form, amount:e.target.value})}
                style={{...inputStyle, fontSize:'1.1rem', fontWeight:'700'}}
                autoFocus/>
            </div>
            <div>
              <label style={labelStyle}>Mode</label>
              <select value={form.mode} onChange={e=>setForm({...form, mode:e.target.value})} style={inputStyle}>
                {Object.entries(modeLabels).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Reference #</label>
              <input placeholder="Cheque / UPI ref" value={form.referenceNumber}
                onChange={e=>setForm({...form, referenceNumber:e.target.value})} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input placeholder="Optional notes" value={form.notes}
                onChange={e=>setForm({...form, notes:e.target.value})} style={inputStyle}/>
            </div>
          </div>
          {/* Split Payment Toggle */}
          <div style={{marginBottom:'12px', display:'flex', alignItems:'center', gap:'10px'}}>
            <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'0.85rem', fontWeight:'600', color:theme.navy}}>
              <input type="checkbox" checked={splitEnabled} onChange={e => setSplitEnabled(e.target.checked)} />
              Split Payment
            </label>
          </div>

          {/* Split Payment Rows */}
          {splitEnabled && (
            <div style={{padding:'12px', backgroundColor:'#f0fdf4', borderRadius:'8px', marginBottom:'12px', borderLeft:`4px solid ${theme.success}`}}>
              <div style={{fontSize:'0.82rem', fontWeight:'700', color:theme.navy, marginBottom:'8px'}}>Split Details</div>
              {splits.map((sp, idx) => (
                <div key={idx} style={{display:'flex', gap:'10px', alignItems:'center', marginBottom:'6px'}}>
                  <span style={{fontSize:'0.82rem', color:theme.textMuted, fontWeight:'600', minWidth:'50px'}}>Row {idx+1}</span>
                  <select value={sp.mode} onChange={e => { const ns=[...splits]; ns[idx]={...ns[idx], mode:e.target.value}; setSplits(ns); }} style={{...inputStyle, width:'140px'}}>
                    {Object.entries(modeLabels).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                  <input type="number" placeholder="Amount" value={sp.amount}
                    onChange={e => { const ns=[...splits]; ns[idx]={...ns[idx], amount:e.target.value}; setSplits(ns); }}
                    style={{...inputStyle, width:'140px', fontWeight:'700'}} />
                </div>
              ))}
              <div style={{fontSize:'0.82rem', color:theme.navy, fontWeight:'700', marginTop:'6px'}}>
                Split Total: {fmtINR(splits.reduce((s,sp) => s + (Number(sp.amount)||0), 0))}
              </div>
            </div>
          )}

          {outstanding.outstanding > 0 && (
            <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:'0 0 10px'}}>
              Outstanding balance: <strong style={{color:theme.danger}}>{fmtINR(outstanding.outstanding)}</strong>
              <button onClick={()=>setForm({...form, amount:outstanding.outstanding})}
                style={{marginLeft:'12px', padding:'3px 10px', border:`1px solid ${theme.primary}`, borderRadius:'6px', cursor:'pointer', backgroundColor:'white', color:theme.primaryDark, fontSize:'0.78rem', fontWeight:'600'}}>
                Pay Full
              </button>
            </p>
          )}
          <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
            <button onClick={() => handleSave('payment')} disabled={saving}
              style={{...btnStyle, marginTop:0, backgroundColor:theme.success}}>
              {saving ? 'Saving...' : '✓ Record Payment'}
            </button>
            <button onClick={() => handleSave('advance')} disabled={saving}
              style={{...btnStyle, marginTop:0, backgroundColor:theme.info}}>
              Advance Payment
            </button>
            {pinelabsConfig && (
              <button onClick={handlePinelabsPay} disabled={pinelabsProcessing}
                style={{...btnStyle, marginTop:0, backgroundColor:'#1a2332'}}>
                {pinelabsProcessing ? '⏳ Processing on Machine...' : '💳 Pay via Pine Labs Machine'}
              </button>
            )}
            {razorpayConfig?.configured && (
              <button onClick={createRazorpayLink} disabled={razorpayLoading}
                style={{...btnStyle, marginTop:0, backgroundColor:'#3395ff'}}>
                {razorpayLoading ? '⏳ Generating…' : '🔗 Razorpay Pay Link'}
              </button>
            )}
          </div>

          {razorpayLink?.shortUrl && (
            <div style={{marginTop:12, padding:12, backgroundColor:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8}}>
              <div style={{fontSize:'0.78rem', fontWeight:700, color:'#1e40af', marginBottom:6}}>
                ✓ Razorpay link ready — share with patient
              </div>
              <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', marginBottom:8}}>
                <input value={razorpayLink.shortUrl} readOnly
                  style={{...inputStyle, flex:1, minWidth:220, fontFamily:'monospace', fontSize:'0.78rem'}} />
                <button onClick={copyRazorpayLink} style={{padding:'7px 12px', borderRadius:6, border:`1px solid ${theme.border}`, background:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:600}}>📋 Copy</button>
                <button onClick={shareOnWhatsApp} style={{padding:'7px 12px', borderRadius:6, border:'none', background:'#25D366', color:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:700}}>💬 WhatsApp</button>
              </div>
              <div style={{fontSize:'0.7rem', color:theme.textMuted}}>
                Status: <strong>{razorpayLink.status}</strong> · Patient ke pay karne ke baad Payment entry automatic ban jayegi (webhook ke through).
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pine Labs Overlay — 3 stages: waiting / success / failed */}
      {pinelabsResult && (
        <div style={{
          position:'fixed', top:0, left:0, width:'100%', height:'100%',
          backgroundColor:'rgba(0,0,0,0.7)', zIndex:4000,
          display:'flex', alignItems:'center', justifyContent:'center',
          animation:'fadeIn 0.3s ease',
        }}>
          <div style={{
            backgroundColor:'white', borderRadius:'20px', padding:'40px 50px',
            textAlign:'center', maxWidth:'460px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
          }}>

            {/* ── WAITING: Payment pushed to machine, waiting for staff confirmation ── */}
            {pinelabsResult.stage === 'waiting' && (<>
              <div style={{
                width:'90px', height:'90px', borderRadius:'50%', margin:'0 auto 20px',
                display:'flex', alignItems:'center', justifyContent:'center',
                background:'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow:'0 8px 30px rgba(245,158,11,0.4)',
                animation:'pulse 1.5s infinite',
              }}>
                <span style={{fontSize:'2.5rem', color:'white'}}>💳</span>
              </div>
              <h2 style={{margin:'0 0 8px', color:'#92400e', fontSize:'1.4rem'}}>
                Waiting for Payment...
              </h2>
              <p style={{margin:'0 0 4px', fontSize:'2rem', fontWeight:'700', color:'#1a2332'}}>
                ₹{(pinelabsResult.amount||0).toLocaleString('en-IN')}
              </p>
              <p style={{margin:'0 0 20px', color:'#6b7785', fontSize:'0.88rem'}}>
                Ask patient to complete payment on the machine.
                <br/>Once done, verify and click <strong>Confirm</strong>.
              </p>
              {pinelabsResult.sandbox && (
                <p style={{margin:'0 0 16px', padding:'4px 12px', backgroundColor:'#fef3c7', borderRadius:'8px', color:'#92400e', fontSize:'0.78rem', fontWeight:'600', display:'inline-block'}}>
                  SANDBOX MODE
                </p>
              )}
              <div style={{display:'flex', gap:'12px', justifyContent:'center'}}>
                <button onClick={handlePinelabsConfirm}
                  style={{padding:'12px 32px', background:'linear-gradient(135deg, #10b981 0%, #059669 100%)', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'700', fontSize:'1rem', boxShadow:'0 4px 14px rgba(16,185,129,0.3)'}}>
                  ✓ Confirm Payment Received
                </button>
                <button onClick={handlePinelabsCancel}
                  style={{padding:'12px 24px', backgroundColor:'#f0f0f0', color:'#555', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'600', fontSize:'0.9rem'}}>
                  ✗ Cancel
                </button>
              </div>
            </>)}

            {/* ── SUCCESS: Staff confirmed ── */}
            {pinelabsResult.stage === 'success' && (<>
              <div style={{
                width:'100px', height:'100px', borderRadius:'50%', margin:'0 auto 20px',
                display:'flex', alignItems:'center', justifyContent:'center',
                background:'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow:'0 8px 30px rgba(16,185,129,0.4)',
                animation:'popIn 0.4s ease',
              }}>
                <span style={{fontSize:'3rem', color:'white', lineHeight:1}}>✓</span>
              </div>
              <h2 style={{margin:'0 0 8px', color:'#065f46', fontSize:'1.6rem', fontWeight:'800'}}>
                Payment Successful!
              </h2>
              <p style={{margin:'0 0 6px', fontSize:'2.2rem', fontWeight:'700', color:'#10b981'}}>
                ₹{(pinelabsResult.amount||0).toLocaleString('en-IN')}
              </p>
              <p style={{margin:0, color:'#6b7785', fontSize:'0.9rem'}}>
                Received from {patientName}
              </p>
            </>)}

            {/* ── FAILED ── */}
            {pinelabsResult.stage === 'failed' && (<>
              <div style={{
                width:'90px', height:'90px', borderRadius:'50%', margin:'0 auto 20px',
                display:'flex', alignItems:'center', justifyContent:'center',
                background:'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                boxShadow:'0 8px 30px rgba(239,68,68,0.4)',
                animation:'popIn 0.4s ease',
              }}>
                <span style={{fontSize:'2.5rem', color:'white'}}>✗</span>
              </div>
              <h2 style={{margin:'0 0 8px', color:'#991b1b', fontSize:'1.4rem'}}>Payment Failed</h2>
              <p style={{margin:'0 0 16px', color:'#6b7785', fontSize:'0.88rem'}}>{pinelabsResult.message}</p>
              <button onClick={()=>setPinelabsResult(null)}
                style={{padding:'10px 30px', backgroundColor:'#f0f0f0', color:'#555', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600'}}>
                Close
              </button>
            </>)}

          </div>
          <style>{`
            @keyframes fadeIn { from{opacity:0} to{opacity:1} }
            @keyframes popIn { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.2)} 100%{transform:scale(1);opacity:1} }
            @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.05)} }
          `}</style>
        </div>
      )}

      {/* History table */}
      {payments.length === 0 ? (
        <div style={{textAlign:'center', color:'#bbb', padding:'50px', backgroundColor:'#fafafa', borderRadius:'10px'}}>
          No payments recorded yet
        </div>
      ) : (
        <table style={{width:'100%', borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Voucher</th>
              <th style={thStyle}>Mode</th>
              <th style={thStyle}>Reference</th>
              <th style={thStyle}>Notes</th>
              <th style={{...thStyle, textAlign:'right'}}>Amount</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p._id}>
                <td style={tdStyle}>{fmtDate(p.date)}</td>
                <td style={tdStyle}>
                  <span style={{fontWeight:'600', color:theme.navy, fontSize:'0.82rem'}}>{p.voucherNumber || '—'}</span>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700',
                    backgroundColor: (modeColors[p.mode]||'#aaa') + '18',
                    color: modeColors[p.mode]||'#aaa',
                  }}>
                    {modeLabels[p.mode]||p.mode}
                  </span>
                </td>
                <td style={tdStyle}>{p.referenceNumber || '—'}</td>
                <td style={tdStyle}>{p.notes || '—'}</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.success, fontSize:'1rem'}}>{fmtINR(p.amount)}</td>
                <td style={tdStyle}>
                  <div style={{display:'flex', gap:'6px'}}>
                    <button onClick={()=>setViewReceipt(p)}
                      style={{padding:'4px 10px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                      🖨 Receipt
                    </button>
                    <button onClick={()=>handleDelete(p._id)}
                      style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Payment Receipt Modal */}
      {viewReceipt && (
        <PaymentReceipt
          payment={viewReceipt}
          patient={{ name: patientName, phone: patientPhone, uhid: patientUhid }}
          outstanding={outstanding}
          clinic={{
            name: clinicSettings?.clinicName || 'Siddharth Dental Hospital',
            address: clinicSettings?.clinicAddress || '',
            phone: clinicSettings?.clinicPhone || '',
            email: clinicSettings?.clinicEmail || '',
            gstin: clinicSettings?.clinicGstin || '',
            upiId: clinicSettings?.upiId || '',
          }}
          onClose={()=>setViewReceipt(null)}
        />
      )}
    </div>
  );
};

export default PaymentSection;
